import React, { useMemo, useState } from "react";
import { ArrowUpDown, ChevronDown, ChevronUp, Search, Inbox } from "lucide-react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { GlassCard } from "@/components/common/GlassCard";
import { EmptyState } from "@/components/common/EmptyState";
import { TableSkeleton } from "@/components/common/Skeletons";
import { cn } from "@/lib/utils";

/**
 * Reusable premium data table.
 * columns: [{ key, header, sortable, searchable, render?, className?, sortAccessor? }]
 */
export function DataTable({
  columns,
  rows,
  loading = false,
  searchable = true,
  searchPlaceholder = "Search…",
  searchTestId,
  rowTestId,
  onRowClick,
  filters = null,
  toolbar = null,
  emptyTitle = "Nothing here yet",
  emptyDescription = "There are no records to display.",
  getRowId = (r) => r.id,
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const searchKeys = columns.filter((c) => c.searchable).map((c) => c.key);

  const processed = useMemo(() => {
    let data = [...rows];
    if (query && searchKeys.length) {
      const q = query.toLowerCase();
      data = data.filter((r) =>
        searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(q))
      );
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const acc = col?.sortAccessor || ((r) => r[sort.key]);
      data.sort((a, b) => {
        const av = acc(a);
        const bv = acc(b);
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number")
          return sort.dir === "asc" ? av - bv : bv - av;
        return sort.dir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }
    return data;
  }, [rows, query, sort, searchKeys, columns]);

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );

  if (loading) return <TableSkeleton rows={6} cols={columns.length} />;

  return (
    <GlassCard className="overflow-hidden">
      {(searchable || filters || toolbar) && (
        <div className="flex flex-col gap-3 border-b border-border/60 p-4 sm:flex-row sm:items-center sm:justify-between">
          {searchable ? (
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid={searchTestId}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="border-border bg-secondary/30 pl-9"
              />
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            {filters}
            {toolbar}
          </div>
        </div>
      )}

      {processed.length === 0 ? (
        <div className="p-6">
          <EmptyState icon={Inbox} title={emptyTitle} description={emptyDescription} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      "px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                      col.className
                    )}
                  >
                    {col.sortable ? (
                      <button
                        onClick={() => toggleSort(col.key)}
                        className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
                      >
                        {col.header}
                        {sort.key === col.key ? (
                          sort.dir === "asc" ? (
                            <ChevronUp className="h-3.5 w-3.5 text-gold" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5 text-gold" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {processed.map((row, i) => (
                <motion.tr
                  key={getRowId(row)}
                  data-testid={rowTestId}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.3) }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-b border-border/40 transition-colors last:border-0",
                    onRowClick && "cursor-pointer hover:bg-secondary/40"
                  )}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cn("px-5 py-4 align-middle", col.cellClassName)}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  );
}
